import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { getAppointments, getNextConsultorio, updateAppointment } from '../lib/api';
import { formatRoomUnit, roomLabelOf } from '../lib/roomLabel';
import {
  appointmentRowHighlightClass,
  hasNoShowBookingWarning,
  needsAttendanceConfirmation,
  noShowBookingHint,
} from '../lib/appointmentBookingHighlight';
import AppointmentBookingLegend from '../components/AppointmentBookingLegend';
import { toast } from 'sonner';
import {
  Calendar as CalendarIconLucide,
  CheckCircle,
  Clock,
  Mail,
  Phone,
  RefreshCw,
  User,
  XCircle,
  ArrowRight,
} from 'lucide-react';
import { formatAppointmentDateOnlyInClinic } from '../lib/clinicTime';

const getStatusBadge = (status) => {
  const styles = {
    pending: 'bg-warning/10 text-warning border-warning/20',
    confirmed: 'bg-success/10 text-success border-success/20',
    cancelled: 'bg-accent/10 text-accent border-accent/20',
    completed: 'bg-primary/10 text-primary border-primary/20',
  };
  const labels = {
    pending: 'Por confirmar',
    confirmed: 'Confirmada',
    cancelled: 'Cancelada',
    completed: 'Completada',
  };
  return (
    <Badge variant="outline" className={styles[status] || styles.pending}>
      {labels[status] || status}
    </Badge>
  );
};

export default function AdminPendingAppointments() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState({ id: null, patientName: '' });
  const [cancelMessage, setCancelMessage] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirmConsultorio, setConfirmConsultorio] = useState(1);
  const [confirmRoomCount, setConfirmRoomCount] = useState(1);
  const [confirmRoomLabel, setConfirmRoomLabel] = useState('Consultorio');
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAppointments('pending');
      setAppointments(Array.isArray(data) ? data : []);
    } catch {
      toast.error('No se pudieron cargar las citas pendientes');
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openConfirmDialog = async (appointment) => {
    setConfirmTarget(appointment);
    setConfirmConsultorio(1);
    setConfirmRoomCount(1);
    setConfirmRoomLabel('Consultorio');
    setConfirmDialogOpen(true);
    if (appointment.appointment_type_id) {
      try {
        const data = await getNextConsultorio(
          appointment.appointment_type_id,
          appointment.date,
          appointment.time,
        );
        setConfirmConsultorio(data.consultorio_number || 1);
        setConfirmRoomCount(data.room_count || 1);
        setConfirmRoomLabel(roomLabelOf(data.room_label));
      } catch {
        /* default 1 */
      }
    }
  };

  const submitConfirmAppointment = async () => {
    if (!confirmTarget?.id) return;
    setConfirmSubmitting(true);
    try {
      await updateAppointment(confirmTarget.id, {
        status: 'confirmed',
        consultorio_number: confirmConsultorio,
      });
      toast.success('Cita confirmada');
      setConfirmDialogOpen(false);
      setConfirmTarget(null);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al confirmar la cita');
    } finally {
      setConfirmSubmitting(false);
    }
  };

  const openCancelDialog = (appointmentId, patientName = '') => {
    setCancelTarget({ id: appointmentId, patientName });
    const first = patientName ? String(patientName).trim().split(/\s+/)[0] : '';
    setCancelMessage(
      `Hola${first ? ` ${first}` : ''}, lamentamos informarte que tu cita en Centro Cultural quedó cancelada. Si deseas otra fecha, escríbenos.`,
    );
    setCancelDialogOpen(true);
  };

  const submitCancelAppointment = async () => {
    if (!cancelTarget.id) return;
    setCancelSubmitting(true);
    try {
      await updateAppointment(cancelTarget.id, {
        status: 'cancelled',
        cancel_whatsapp_message: cancelMessage.trim(),
      });
      toast.success('Cita cancelada');
      setCancelDialogOpen(false);
      setCancelTarget({ id: null, patientName: '' });
      await load();
    } catch {
      toast.error('Error al cancelar la cita');
    } finally {
      setCancelSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="admin-pending-appointments">
        <div className="animate-pulse text-muted-foreground">Cargando citas por confirmar…</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-pending-appointments">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Citas por confirmar</h1>
          <p className="text-muted-foreground">
            Solicitudes pendientes de aprobación. Al confirmar, el paciente puede recibir aviso por WhatsApp según
            configuración.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" className="gap-2" onClick={load}>
            <RefreshCw className="w-4 h-4" />
            Actualizar
          </Button>
          <Button type="button" variant="outline" asChild className="gap-2">
            <Link to="/admin/citas">
              Ir a todas las citas
              <ArrowRight className="w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>

      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="font-heading text-xl">Listado</CardTitle>
          <CardDescription>
            Mostrando {appointments.length} cita
            {appointments.length !== 1 ? 's' : ''} pendiente
            {appointments.length !== 1 ? 's' : ''} de confirmación
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Paciente</TableHead>
                <TableHead>Contacto</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Origen</TableHead>
                <TableHead>Fecha y hora</TableHead>
                <TableHead>Motivo</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {appointments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                    No hay citas pendientes por confirmar. Buen trabajo.
                  </TableCell>
                </TableRow>
              ) : (
                appointments.map((appointment) => (
                  <TableRow
                    key={appointment.id}
                    data-testid={`pending-appointment-row-${appointment.id}`}
                    className={appointmentRowHighlightClass(appointment)}
                    title={noShowBookingHint(appointment) || undefined}
                  >
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-muted-foreground shrink-0" />
                          <span className="font-medium">{appointment.patient_name}</span>
                        </div>
                        {needsAttendanceConfirmation(appointment) ? (
                          <Badge
                            variant="outline"
                            className="w-fit text-xs border-sky-500/50 bg-sky-100/50 text-sky-900 dark:text-sky-100"
                          >
                            Asistencia sin confirmar
                          </Badge>
                        ) : null}
                        {hasNoShowBookingWarning(appointment) ? (
                          <Badge
                            variant="outline"
                            className="w-fit text-xs border-amber-500/50 bg-amber-100/50 text-amber-900 dark:text-amber-100"
                          >
                            {appointment.booking_flag === 'transfer_required'
                              ? `Transferencia (${appointment.booking_no_show_count ?? '?'} inasist.)`
                              : `Inasistencia previa (${appointment.booking_no_show_count ?? '?'})`}
                          </Badge>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Mail className="w-3 h-3" />
                          {appointment.patient_email || '—'}
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Phone className="w-3 h-3" />
                          {appointment.patient_phone}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-normal whitespace-nowrap">
                        {appointment.appointment_type_label || appointment.appointment_type_id || '—'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-normal whitespace-nowrap">
                        {appointment.booking_source === 'admin'
                          ? 'Panel admin'
                          : appointment.booking_source === 'whatsapp'
                            ? 'WhatsApp'
                            : 'Sitio web'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-1">
                          <CalendarIconLucide className="w-4 h-4 text-primary" />
                          <span>{formatAppointmentDateOnlyInClinic(appointment.date, 'EEE, d MMM yyyy')}</span>
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Clock className="w-4 h-4" />
                          <span>{appointment.time}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-muted-foreground max-w-[200px] truncate">
                        {appointment.reason || 'Sin especificar'}
                      </p>
                    </TableCell>
                    <TableCell>{getStatusBadge(appointment.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-success hover:text-success hover:bg-success/10"
                          onClick={() => openConfirmDialog(appointment)}
                          data-testid={`pending-confirm-${appointment.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Confirmar
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-accent hover:text-accent hover:bg-accent/10"
                          onClick={() => openCancelDialog(appointment.id, appointment.patient_name)}
                          data-testid={`pending-cancel-${appointment.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Cancelar
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog
        open={confirmDialogOpen}
        onOpenChange={(open) => {
          setConfirmDialogOpen(open);
          if (!open) setConfirmTarget(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Confirmar cita</DialogTitle>
            <DialogDescription>
              {confirmTarget
                ? `${confirmTarget.patient_name} — ${confirmTarget.date} ${confirmTarget.time}`
                : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="confirm-consultorio">{confirmRoomLabel}</Label>
            <select
              id="confirm-consultorio"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={confirmConsultorio}
              onChange={(e) => setConfirmConsultorio(Number(e.target.value))}
            >
              {Array.from({ length: confirmRoomCount }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>
                  {formatRoomUnit(confirmRoomLabel, n)}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              Por defecto se asigna el siguiente {confirmRoomLabel.toLowerCase()} libre para este horario y
              tipo de servicio.
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setConfirmDialogOpen(false)} disabled={confirmSubmitting}>
              Cancelar
            </Button>
            <Button type="button" onClick={submitConfirmAppointment} disabled={confirmSubmitting}>
              {confirmSubmitting ? 'Confirmando…' : 'Confirmar cita'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={cancelDialogOpen}
        onOpenChange={(open) => {
          setCancelDialogOpen(open);
          if (!open) setCancelTarget({ id: null, patientName: '' });
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Cancelar cita</DialogTitle>
            <DialogDescription>
              Si escribes un mensaje y el paciente tiene teléfono, se enviará por WhatsApp al guardar.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="pending-cancel-wa">Mensaje para el paciente (WhatsApp)</Label>
            <Textarea
              id="pending-cancel-wa"
              rows={5}
              value={cancelMessage}
              onChange={(e) => setCancelMessage(e.target.value)}
              className="resize-y min-h-[120px]"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setCancelDialogOpen(false)} disabled={cancelSubmitting}>
              Volver
            </Button>
            <Button type="button" variant="destructive" onClick={submitCancelAppointment} disabled={cancelSubmitting}>
              {cancelSubmitting ? 'Cancelando…' : 'Cancelar cita'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
